import store from 'redux/store';
import { deleteSchedulingWindow } from 'redux/slices/scheduling_windowsSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditSchedulingWindow } from './edit/showEditSchedulingWindow';
import { showOpenPeriodsPreview } from './showOpenPeriodsPreview';

type SchedulingWindowEntity = Extract<ActionEntity, { type: 'scheduling_window' }>;

const isAllSchedulingWindow = (entities: readonly ActionEntity[]): entities is readonly SchedulingWindowEntity[] =>
  entities.every((entity) => entity.type === 'scheduling_window');

/**
 * Action definitions for scheduling windows.
 *
 * `createSchedulingWindow` / `addToTransportSchedulingWindow` are scoped (always
 * present, fully-qualified); `edit` / `duplicate` / `previewOpenPeriods` (single-only)
 * and `delete` are contextual. Every action that changes a window refreshes the
 * list on success: the slice holds no create/update thunks, so a saved row only
 * reaches the list by refetching. `previewOpenPeriods` is the exception — it
 * reads, changes nothing, and so does not refresh.
 */
export const schedulingWindowActionDefinitions: ActionDefinitions = [
  {
    id: 'createSchedulingWindow',
    scope: 'type',
    label: 'New Window',
    resolve: (ctx) => () => void showEditSchedulingWindow({ mode: 'create', createIn: ctx.createIn }).then((saved) => saved && ctx.refresh()),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllSchedulingWindow(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditSchedulingWindow({ mode: 'edit', id: only.id }).then((saved) => saved && ctx.refresh());
    },
  },
  {
    id: 'duplicate',
    label: 'Duplicate',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllSchedulingWindow(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditSchedulingWindow({ mode: 'duplicate', id: only.id }).then((saved) => saved && ctx.refresh());
    },
  },
  {
    // Reached from the list, not from the edit dialog: the evaluate route reads the
    // database, so a preview can only ever show the window as SAVED. Inside the
    // dialog it would look like feedback on the rules currently on screen and
    // silently ignore every unsaved edit.
    id: 'previewOpenPeriods',
    label: 'Preview Open Periods',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllSchedulingWindow(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      // Read-only: no refresh, nothing changed.
      return () => void showOpenPeriodsPreview(only.id);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllSchedulingWindow(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteSchedulingWindow({ zone_name: entity.zoneName, window_name: entity.name })), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this scheduling window' : `these ${entities.length} scheduling windows`}?`,
      });
    },
  },
  {
    id: 'addToTransportSchedulingWindow',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllSchedulingWindow(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'scheduling_window',
        entities.map((entity) => entity.id),
        { objectLabel: single ? `${single.zoneName} / ${single.name}` : 'Selected scheduling windows' }
      );
    },
  },
];
